import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clapperboard, Heart, Play, Star } from 'lucide-react';
import axios from 'axios';
import { BACKEND_URL } from '@/lib/backendUrl';
import { usePublicCopy } from '@/i18n/publicCopy';
import { Section } from './HomeSections';

const card = { backgroundColor: 'var(--bg-surface)', border: '1px solid var(--border)' };
const kicker = 'text-xs tracking-[0.2em] uppercase mb-3';

function NameOfTheDay({ card: c, s }) {
  if (!c?.share_id) return null;
  return (
    <Link to={`/portrait-card/${c.share_id}`} className="rounded-2xl p-5 flex gap-5 items-center transition-transform hover:-translate-y-0.5" style={card} data-testid="home-name-of-the-day">
      <img src={`${BACKEND_URL}/api/portrait-cards/${c.share_id}/card?style=light`} alt={`${c.name_analyzed} name portrait card`}
        className="w-20 sm:w-24 rounded-lg shadow-md shrink-0" style={{ aspectRatio: '9 / 16', objectFit: 'cover' }} loading="lazy" />
      <div className="min-w-0 flex-1">
        <p className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-widest" style={{ color: 'var(--accent)' }}><Star className="w-3 h-3" aria-hidden="true" /> {s.name_kicker}</p>
        <p className="font-heading text-3xl sm:text-4xl font-light mt-1" style={{ color: 'var(--text-primary)' }} data-testid="home-name-of-the-day-name">{c.name_analyzed}</p>
        <p className="text-sm leading-relaxed mt-1" style={{ color: 'var(--text-secondary)' }}>{s.name_body}</p>
        <span className="inline-flex items-center gap-1 text-sm mt-3" style={{ color: 'var(--accent)' }}>{s.name_cta} <ArrowRight className="w-4 h-4" aria-hidden="true" /></span>
      </div>
    </Link>
  );
}

function ClipWall({ clip, s }) {
  if (!clip) return null;
  return (
    <Link to={`/clips?clip=${clip.draft_id}`} className="rounded-2xl p-5 flex gap-5 items-center transition-transform hover:-translate-y-0.5" style={card} data-testid="home-clip-wall">
      <div className="relative w-20 sm:w-24 rounded-lg overflow-hidden bg-black shrink-0" style={{ aspectRatio: '9 / 16' }}>
        <img src={`${BACKEND_URL}/api/wall-of-clips/${clip.draft_id}/thumb`} alt="" className="w-full h-full object-cover" loading="lazy" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        <span className="absolute inset-0 flex items-center justify-center"><span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(0,0,0,0.55)' }}><Play className="w-4 h-4 text-white" fill="#fff" /></span></span>
      </div>
      <div className="min-w-0 flex-1">
        <p className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-widest" style={{ color: 'var(--accent)' }}><Clapperboard className="w-3 h-3" aria-hidden="true" /> {s.clip_kicker}</p>
        <p className="font-heading text-xl sm:text-2xl leading-snug mt-1 line-clamp-2" style={{ color: 'var(--text-primary)' }} data-testid="home-clip-wall-hook">{clip.hook || clip.caption}</p>
        <p className="text-sm leading-relaxed mt-1" style={{ color: 'var(--text-secondary)' }}>{s.clip_body}{clip.likes ? <span className="inline-flex items-center gap-1 ml-2"><Heart className="w-3.5 h-3.5" aria-hidden="true" /> {clip.likes}</span> : null}</p>
        <span className="inline-flex items-center gap-1 text-sm mt-3" style={{ color: 'var(--accent)' }}>{s.clip_cta} <ArrowRight className="w-4 h-4" aria-hidden="true" /></span>
      </div>
    </Link>
  );
}

/** Home spotlight: the Name (card) of the Day + the clip wall's best-loved clip. Hidden when neither exists. */
export function CommunitySpotlight() {
  const { c } = usePublicCopy();
  const s = c.spotlight;
  const [cardOfDay, setCardOfDay] = useState(null);
  const [clip, setClip] = useState(null);
  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/portrait-cards/card-of-the-day`).then((r) => setCardOfDay(r.data.card || null)).catch(() => {});
    axios.get(`${BACKEND_URL}/api/wall-of-clips`).then((r) => setClip((r.data.clips || [])[0] || null)).catch(() => {});
  }, []);
  if (!cardOfDay?.share_id && !clip) return null;
  return (
    <Section id="spotlight" label={s.h2} className="border-t">
      <p className={kicker} style={{ color: 'var(--accent)' }}>{s.kicker}</p>
      <h2 className="font-heading text-3xl sm:text-4xl font-light tracking-tight" style={{ color: 'var(--text-primary)' }}>{s.h2}</h2>
      <div className="grid md:grid-cols-2 gap-5 mt-8" data-testid="home-spotlight">
        <NameOfTheDay card={cardOfDay} s={s} />
        <ClipWall clip={clip} s={s} />
      </div>
    </Section>
  );
}
